<script async setup>
import { ref, defineProps, computed, watch } from "vue";
import KdFormElement from "./KdFormElement.vue";
import KdIcon from "./KdIcon.vue";

const props = defineProps({
    name: {
        type: String,
        default: "",
    },
    label: {
        type: String,
        default: "",
    },
    error: {
        type: String,
        default: "",
    },
    required: {
        type: Boolean,
        default: false,
    },
    value: {
        type: [String, Number],
        default: "",
    },
    type: {
        type: String,
        default: "text",
    },
    placeholder: {
        type: String,
        default: "",
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    area: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: String,
        default: "",
    },
    iconClass: {
        type: String,
        default: "",
    },
    additionalClass: {
        type: String,
        default: "",
    },
    variant: {               
        type: String,
        default: "default",
    },
});

// Emits
const emit = defineEmits(["update:value"]);

const localValue = ref(props.value);

// Watch for external changes to modelValue and update localValue
watch(
    () => props.value,
    (newValue) => {
        localValue.value = newValue;
    }
);

// Emit changes to the parent when localValue changes
watch(localValue, (newValue) => {
    emit("update:value", newValue);
});

const isActive = computed(() => {
    return isFocused.value || !!localValue.value;
});

const isFocused = ref(false);

const labelClass = computed(() => (isActive.value ? "text-ts-black" : "text-ts-gray"));

const inputClasses = computed(() => {
  const base = "w-full text-sm border focus:outline-0 focus:ring-0 focus:ring-offset-0";
  const iconPad = props.icon ? "pe-7" : "";
  const bg = "bg-transparent";
  const dynamicBorderBase = props.error ? "border-red" : (isActive.value ? "border-ts-black" : "border-ts-gray");
  const dynamicBorder = `${dynamicBorderBase} focus:border-ts-black`;
  const border = props.variant === "white" || props.variant === "transparent-white" ? "border-ts-white focus:border-ts-white" : props.variant === "black" ? "border-ts-black focus:border-ts-black" : dynamicBorder;
  const pad = props.type === "search" ? "px-2 py-0.5 rounded-full" : "px-3 py-1.25 rounded-lg";
  return [base, bg, iconPad, border, pad, props.additionalClass].join(" ");
});
</script>

<template>
    <kd-form-element
        :label="props.label"
        :error="props.error"
        :required="props.required"
        :label-class="labelClass"
    >
        <textarea
            v-if="props.area"
            v-model="localValue"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :name="props.name"
            :placeholder="props.placeholder"
            :readonly="props.readonly"
            :required="props.required"
            :class="inputClasses"
        />
        <input
            v-else
            v-model="localValue"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :name="props.name"
            :type="props.type"
            :placeholder="props.placeholder"
            :readonly="props.readonly"
            :required="props.required"
            :class="inputClasses"
        >
        <kd-icon
            v-if="props.icon"
            :icon="props.icon"
            class="absolute top-1 right-1 z-10"
            :class="iconClass"
            size="28"
        />
    </kd-form-element>
</template>
